import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import API_CONFIG from './config';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // Start with 1 second
const TIMEOUT = 30000; // 30 seconds for production cold starts

// Track retry state using WeakMap to avoid TypeScript issues
const retryState = new WeakMap<InternalAxiosRequestConfig, boolean>();

// Track whether a token refresh is already in-flight (dedup concurrent 401s)
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}
function onTokenRefreshed(newToken: string | null) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

/**
 * Attempt to silently refresh the JWT. Returns the new token or null on failure.
 */
async function attemptTokenRefresh(): Promise<string | null> {
  const currentToken = localStorage.getItem('auth_token');
  if (!currentToken) return null;
  try {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}/auth/refresh`,
      {},
      { headers: { Authorization: `Bearer ${currentToken}`, 'Content-Type': 'application/json' } }
    );
    const newToken: string = response.data?.access_token;
    if (newToken) {
      localStorage.setItem('auth_token', newToken);
      return newToken;
    }
    return null;
  } catch {
    return null;
  }
}

// Helper function to delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Paths that should NOT be retried (e.g. sending emails – retrying causes duplicates)
const NO_RETRY_PATHS = ['/api/outreach/sends/'];

// Helper function to check if error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  if (!error.response) {
    // Network errors or timeouts are retryable
    return true;
  }

  // Never retry certain paths (POST actions with side effects)
  const url = error.config?.url || '';
  if (NO_RETRY_PATHS.some(p => url.includes(p))) {
    return false;
  }
  
  const status = error.response.status;
  // Retry on 5xx errors, 429 (rate limit), and 408 (timeout)
  return status >= 500 || status === 429 || status === 408;
};

// Retry interceptor
const retryRequest = async (
  config: InternalAxiosRequestConfig,
  retriesLeft: number = MAX_RETRIES
): Promise<any> => {
  try {
    return await axios(config);
  } catch (error) {
    const axiosError = error as AxiosError;
    
    // Don't retry if no retries left or error is not retryable
    if (retriesLeft <= 0 || !isRetryableError(axiosError)) {
      throw error;
    }
    
    // Calculate exponential backoff delay
    const delayMs = RETRY_DELAY * Math.pow(2, MAX_RETRIES - retriesLeft);
    console.log(`Request failed, retrying in ${delayMs}ms... (${retriesLeft} retries left)`);
    
    await delay(delayMs);
    return retryRequest(config, retriesLeft - 1);
  }
};

// Create an axios instance with base URL and default headers
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: TIMEOUT,
  // Serialize array params as repeated keys (therapy_interest=a&therapy_interest=b)
  // instead of indexed brackets (therapy_interest[0]=a) which FastAPI doesn't recognise.
  paramsSerializer: {
    indexes: null,
  },
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors and retries
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & { _retried401?: boolean };
    
    // Handle 401 - try a silent token refresh before giving up
    if (error.response?.status === 401 && config && !config._retried401) {
      // Don't try to refresh if the failing request was the refresh call itself
      if (config.url?.includes('/auth/refresh')) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another request is already refreshing – queue this one
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken: string | null) => {
            if (newToken) {
              config.headers.Authorization = `Bearer ${newToken}`;
              config._retried401 = true;
              resolve(axios(config));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;
      const newToken = await attemptTokenRefresh();
      isRefreshing = false;

      if (newToken) {
        onTokenRefreshed(newToken);
        config.headers.Authorization = `Bearer ${newToken}`;
        config._retried401 = true;
        return axios(config);
      }

      // Notify queued subscribers that refresh failed
      onTokenRefreshed(null);

      // Refresh failed – force logout
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Retry logic for retryable errors
    if (config && isRetryableError(error) && !retryState.get(config)) {
      retryState.set(config, true);
      return retryRequest(config);
    }
    
    return Promise.reject(error);
  }
);

export default api;
